import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/auth/useAuth';
import {
  getEnvironmentSettings,
  isDraftSettingsEnabled,
  rollbackEnvironmentSettingChanges,
  updateEnvironmentSettings,
  upsertEnvironmentSettings,
  upsertEnvironmentSettingsWithSnapshot,
  withDraftSettingsEnabled,
} from '@/services/environmentSettingsApi';
import type {
  EnvironmentSetting,
  EnvironmentSettingsPutPayload,
  EnvironmentSettingsRollbackRequest,
} from '@/services/environmentSettingsApi';
import { isLocalMode } from '@/services/localRuntimeClient';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { useMemo } from 'react';

/**
 * Fetches environment settings for an application.
 * Returns the settings array via React Query.
 */
export const useEnvironmentSettings = (projectName: string, appName: string, isDraftMode: boolean) => {
  const { getOttoToken, isAuthenticated } = useAuth();
  // The draft slot only exists when the feature flag is on; otherwise every
  // read collapses to the production slot (and its cache key).
  const draftSettingsEnabled = useFeatureFlag('enableDraftSettings');
  const effectiveIsDraftMode = isDraftMode && draftSettingsEnabled;

  return useQuery({
    queryKey: ['environmentSettings', projectName, appName, effectiveIsDraftMode],
    queryFn: async () => {
      // In local mode the API omits the Authorization header, so skip the
      // async token fetch entirely. Matches the pattern in other hooks
      // (e.g., useAppsInProject).
      if (isLocalMode()) {
        return getEnvironmentSettings(projectName, appName, effectiveIsDraftMode, '');
      }
      const token = await getOttoToken();
      if (!token) {
        throw new Error('Failed to acquire Otto token');
      }
      return getEnvironmentSettings(projectName, appName, effectiveIsDraftMode, token);
    },
    enabled: (isAuthenticated || isLocalMode()) && !!projectName && !!appName,
    staleTime: 30 * 1000, // 30 seconds
    // The env-vars editor holds unsaved local edits in component state; an
    // auto-refetch (focus change, reconnect, or stale interval) would replace
    // those edits with server data and silently discard the user's work.
    // Refetches still happen on explicit user action (Refresh). Saves update
    // the cache directly via setQueryData (no background refetch on save).
    refetchInterval: false,
  });
};

/**
 * Mutation hook to update environment settings.
 *
 * On success, updates the query cache directly with the PUT response via
 * `setQueryData` instead of invalidating + refetching. The ACA backend
 * re-deploys the container on every env-var write, so a GET fired right
 * after the PUT often returns stale (pre-deploy) data. Caching the PUT
 * response keeps the UI consistent without a second round-trip.
 */
export const useUpdateEnvironmentSettings = (projectName: string, appName: string, isDraftMode: boolean, prodSettings: EnvironmentSetting[]) => {
  const { getOttoToken } = useAuth();
  const queryClient = useQueryClient();
  const draftSettingsEnabled = useFeatureFlag('enableDraftSettings');
  const effectiveIsDraftMode = isDraftMode && draftSettingsEnabled;
  // Only synthesize the phantom-enabled production cache entry when the service
  // would actually bootstrap the runtime flag — i.e. this write targets the
  // draft slot and prod doesn't already have it. The service only bootstraps
  // on draft-slot writes, so a production save (effectiveIsDraftMode false)
  // must never synthesize this entry, or it would clobber the just-saved
  // production settings with the stale pre-save snapshot.
  const shouldResetProdSettings = useMemo(
    () => effectiveIsDraftMode && !isDraftSettingsEnabled(prodSettings),
    [effectiveIsDraftMode, prodSettings],
  );

  return useMutation({
    mutationFn: async (payload: EnvironmentSettingsPutPayload) => {
      // Match the pattern used by other local-mode hooks: skip the token
      // fetch in local mode since the API ignores Authorization there.
      if (isLocalMode()) {
        return updateEnvironmentSettings(projectName, appName, effectiveIsDraftMode, '', payload, prodSettings);
      }
      const token = await getOttoToken();
      if (!token) {
        throw new Error('Failed to acquire Otto token');
      }
      return updateEnvironmentSettings(projectName, appName, effectiveIsDraftMode, token, payload, prodSettings);
    },
    onSuccess: (data) => {
      // Update the query cache directly with the PUT response instead of
      // invalidating + refetching. The ACA backend re-deploys the container
      // on every env-var write; a GET fired immediately after the PUT often
      // returns stale data (before the deploy propagates), which overwrites
      // the user's freshly-saved state and makes new variables vanish.
      queryClient.setQueryData(
        ['environmentSettings', projectName, appName, effectiveIsDraftMode],
        data.properties.settings,
      );

      if (shouldResetProdSettings) {
        // The write just enabled the draft flag on the production slot. An
        // immediate GET can return stale data while the ACA container
        // re-deploys, so optimistically write the known production collection
        // (existing settings + enabled flag) into the cache. This keeps the
        // Production tab in sync when the user switches to it.
        queryClient.setQueryData(
          ['environmentSettings', projectName, appName, /* isDraftMode */ false],
          withDraftSettingsEnabled(prodSettings),
        );
      }
    },
  });
};

/**
 * Mutation hook to upsert a small batch of environment settings without
 * disturbing the others. Used by flows (e.g. service-provider connection
 * creation) that need to add or update specific keys imperatively; the
 * `useUpdateEnvironmentSettings` hook above is a raw PUT with replace
 * semantics and is unsuitable for incremental writes.
 */
export const useUpsertEnvironmentSettings = (projectName: string, appName: string, isDraftMode: boolean, prodSettings: EnvironmentSetting[]) => {
  const { getOttoToken } = useAuth();
  const queryClient = useQueryClient();
  const draftSettingsEnabled = useFeatureFlag('enableDraftSettings');
  const effectiveIsDraftMode = isDraftMode && draftSettingsEnabled;
  // Only synthesize the phantom-enabled production cache entry for writes
  // that actually target the draft slot (the service only bootstraps the
  // flag on draft-slot writes); otherwise a production save would clobber
  // its own just-saved settings with the stale pre-save snapshot.
  const shouldResetProdSettings = useMemo(
    () => effectiveIsDraftMode && !isDraftSettingsEnabled(prodSettings),
    [effectiveIsDraftMode, prodSettings],
  );

  return useMutation({
    mutationFn: async (additions: Record<string, string>) => {
      if (isLocalMode()) {
        return upsertEnvironmentSettings(projectName, appName, effectiveIsDraftMode, '', additions, prodSettings);
      }
      const token = await getOttoToken();
      if (!token) {
        throw new Error('Failed to acquire Otto token');
      }
      return upsertEnvironmentSettings(projectName, appName, effectiveIsDraftMode, token, additions, prodSettings);
    },
    onSuccess: (settings) => {
      queryClient.setQueryData(
        ['environmentSettings', projectName, appName, effectiveIsDraftMode],
        settings,
      );

      if (shouldResetProdSettings) {
        // The write just enabled the draft flag on the production slot. An
        // immediate GET can return stale data while the ACA container
        // re-deploys, so optimistically write the known production collection
        // (existing settings + enabled flag) into the cache. This keeps the
        // Production tab in sync when the user switches to it.
        queryClient.setQueryData(
          ['environmentSettings', projectName, appName, /* isDraftMode */ false],
          withDraftSettingsEnabled(prodSettings),
        );
      }
    },
  });
};

/**
 * Upsert settings while returning the fresh pre-write snapshot used by the
 * service. This supports multi-file edits that must restore settings if their
 * subsequent persistence step fails.
 */
export const useTransactionalUpsertEnvironmentSettings = (
  projectName: string,
  appName: string,
  isDraftMode: boolean,
  prodSettings: EnvironmentSetting[],
) => {
  const { getOttoToken } = useAuth();
  const queryClient = useQueryClient();
  const draftSettingsEnabled = useFeatureFlag('enableDraftSettings');
  const effectiveIsDraftMode = isDraftMode && draftSettingsEnabled;
  // Only synthesize the phantom-enabled production cache entry for writes
  // that actually target the draft slot (the service only bootstraps the
  // flag on draft-slot writes); otherwise a production save would clobber
  // its own just-saved settings with the stale pre-save snapshot.
  const shouldResetProdSettings = useMemo(
    () => effectiveIsDraftMode && !isDraftSettingsEnabled(prodSettings),
    [effectiveIsDraftMode, prodSettings],
  );

  return useMutation({
    mutationFn: async (additions: Record<string, string>) => {
      if (isLocalMode()) {
        return upsertEnvironmentSettingsWithSnapshot(
          projectName,
          appName,
          effectiveIsDraftMode,
          '',
          additions,
          prodSettings,
        );
      }
      const token = await getOttoToken();
      if (!token) {
        throw new Error('Failed to acquire Otto token');
      }
      return upsertEnvironmentSettingsWithSnapshot(
        projectName,
        appName,
        effectiveIsDraftMode,
        token,
        additions,
        prodSettings,
      );
    },
    onSuccess: ({ settings }) => {
      queryClient.setQueryData(
        ['environmentSettings', projectName, appName, effectiveIsDraftMode],
        settings,
      );
      if (shouldResetProdSettings) {
        queryClient.setQueryData(
          ['environmentSettings', projectName, appName, false],
          withDraftSettingsEnabled(prodSettings),
        );
      }
    },
  });
};

export const useRollbackEnvironmentSettingChanges = (
  projectName: string,
  appName: string,
  isDraftMode: boolean,
  prodSettings: EnvironmentSetting[],
) => {
  const { getOttoToken } = useAuth();
  const queryClient = useQueryClient();
  const draftSettingsEnabled = useFeatureFlag('enableDraftSettings');
  const effectiveIsDraftMode = isDraftMode && draftSettingsEnabled;

  return useMutation({
    mutationFn: async (request: EnvironmentSettingsRollbackRequest) => {
      if (isLocalMode()) {
        return rollbackEnvironmentSettingChanges(
          projectName,
          appName,
          effectiveIsDraftMode,
          '',
          request,
          prodSettings,
        );
      }
      const token = await getOttoToken();
      if (!token) {
        throw new Error('Failed to acquire Otto token');
      }
      return rollbackEnvironmentSettingChanges(
        projectName,
        appName,
        effectiveIsDraftMode,
        token,
        request,
        prodSettings,
      );
    },
    onSuccess: (settings) => {
      queryClient.setQueryData(
        ['environmentSettings', projectName, appName, effectiveIsDraftMode],
        settings,
      );
    },
  });
};
